/**
 * Query keys, the shared LootClient singleton, and queryOptions builders —
 * plain functions with no React in them, so hooks and non-React code paths
 * (LootClient.onSessionExpired, the imperative session mint) share one cache.
 */
import type {
  BalancePack,
  BattleListResponse,
  BattleResponse,
  BattleStatus,
  ForgeBoxListResponse,
  ForgeBoxSort,
  ForgeImagesResponse,
  ForgeItemsResponse,
  InventoryItemStatus,
  MyForgeResponse,
  RealtimeTokenResponse,
  ShippingAddressResponse,
  StorefrontBanner,
  UnclaimedRevealResponse,
} from "@loot-os/api-contract";
import { type ErrorCode, LootApiError, LootClient } from "@loot-os/client";
import { verifyOpen } from "@loot-os/client/verify";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { fetchWithAnalyticsContext } from "./analyticsGate.js";
import { LOOT_PROXY_BASE, PROXIED_PUBLISHABLE_KEY } from "./config.js";
import { queryClient } from "./queryClient.js";
import { captureServerTime } from "./serverClock.js";

/** Everything under "private" is token-scoped: sign-out removes it in one sweep. */
export const qk = {
  tenant: ["tenant-config"] as const,
  authMe: ["auth-me"] as const,
  private: ["private"] as const,
  session: ["private", "loot-session"] as const,
  balance: ["private", "balance"] as const,
  lootMe: ["private", "loot-me"] as const,
  opens: ["private", "opens"] as const,
  inventory: (status: InventoryItemStatus | "all") => ["private", "inventory", status] as const,
  balancePacks: ["private", "balance-packs"] as const,
  // Banners depend on the visitor's spend band, so the key names the viewer ("anon" signed out)
  // and a sign-in or sign-out fetches the right set instead of serving the other one from cache.
  banners: (viewer: string) => ["banners", viewer] as const,
  purchase: (sid: string) => ["private", "purchase", sid] as const,
  boxes: ["boxes", "list"] as const,
  box: (id: string) => ["boxes", "detail", id] as const,
  verification: (openId: string) => ["fairness", "verification", openId] as const,
  seed: (date: string) => ["fairness", "seed", date] as const,
  // Battles. The detail carries `me`, so it is token-scoped. The lobby folds in the signed-in
  // shopper's own private battles, so its key names the viewer ("anon" when signed out) and a
  // sign-in or sign-out fetches the right page instead of serving the other one from cache.
  battles: (viewer: string, status?: BattleStatus) =>
    ["battles", "list", viewer, status ?? "all"] as const,
  battle: (battleId: string) => ["private", "battles", "detail", battleId] as const,
  myBattles: ["private", "battles", "mine"] as const,
  // The realtime token names the shopper it was minted for (their own notification channel), so
  // the key names the viewer ("anon" signed out) and a sign-in or sign-out mints a new one.
  realtimeTokens: ["private", "realtime-token"] as const,
  realtimeToken: (viewer: string) => ["private", "realtime-token", viewer] as const,
  unclaimedReveal: ["private", "unclaimed-reveal"] as const,
  shippingAddress: ["private", "shipping-address"] as const,
  favorites: ["private", "favorites"] as const,
  // Forge. The browse grid and the preset images are public; the item search (a session route)
  // and the shopper's own boxes are token-scoped.
  forgeBoxes: (sort: ForgeBoxSort) => ["forge", "boxes", sort] as const,
  forgeImages: ["forge", "images"] as const,
  forgeItems: (search: ForgeItemSearch) =>
    [
      "private",
      "forge",
      "items",
      search.q,
      search.minCents ?? null,
      search.maxCents ?? null,
    ] as const,
  myForge: ["private", "forge", "mine"] as const,
  // Public: what any shopper may see of a player, so it survives a sign-out.
  profile: (endUserId: string) => ["profiles", endUserId] as const,
};

/**
 * The one LootClient for the whole app. Its config is constant (the BFF
 * injects the real publishable key server-side), and a session 401 re-mints
 * through the same cache the session query uses.
 */
export const lootClient = new LootClient({
  publishableKey: PROXIED_PUBLISHABLE_KEY,
  baseUrl: LOOT_PROXY_BASE,
  // While the analytics gate is open, each call says so; see `analyticsGate.ts`.
  fetch: fetchWithAnalyticsContext,
  onSessionExpired: () => mintFreshSessionToken(),
});

/** Re-mint when less than a minute of validity remains. */
const EXPIRY_SLACK_MS = 60_000;

type SessionData = { token: string; expiresAtMs: number } | null;

export const sessionQueryOptions = () =>
  queryOptions({
    queryKey: qk.session,
    // A failed mint means "signed out", not "try again" — and the client
    // already retries a 401 once itself, so never stack retries on top.
    retry: false,
    gcTime: 30 * 60_000,
    // The token's TTL is the staleTime: fresh until 60s before expiry.
    staleTime: (query) => {
      const data = query.state.data;
      return data ? Math.max(0, data.expiresAtMs - Date.now() - EXPIRY_SLACK_MS) : 0;
    },
    queryFn: async (): Promise<SessionData> => {
      const res = await fetch("/api/loot/session", { method: "POST" });
      if (!res.ok) {
        lootClient.setSessionToken(null);
        return null;
      }
      const body = (await res.json()) as { token: string; expiresAt: string };
      // Set the client token synchronously so callers can fetch right away
      // (consumers re-render from the query data afterwards).
      lootClient.setSessionToken(body.token);
      return { token: body.token, expiresAtMs: Date.parse(body.expiresAt) };
    },
  });

/**
 * Mint (or reuse) a Loot session token. fetchQuery returns the cached token
 * while it's inside its TTL and dedupes concurrent callers onto one POST.
 */
export async function mintSessionToken(): Promise<string | null> {
  try {
    const data = await queryClient.fetchQuery(sessionQueryOptions());
    return data?.token ?? null;
  } catch {
    return null;
  }
}

/**
 * For onSessionExpired: the server says the token is dead even if we still
 * think it's fresh (revocation), so force staleness before refetching.
 */
export async function mintFreshSessionToken(): Promise<string | null> {
  await queryClient.invalidateQueries({ queryKey: qk.session, refetchType: "none" });
  return mintSessionToken();
}

export interface ShopperUser {
  id: string;
  email: string;
  /**
   * What the Messenger boots with, minted by the platform: the shopper id and its Identity
   * Verification hash. null when this storefront has chat off (or an older BFF omitted it).
   */
  intercom: { userId: string; userHash: string } | null;
}

export const authMeQueryOptions = () =>
  queryOptions({
    queryKey: qk.authMe,
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<ShopperUser | null> => {
      const res = await fetch("/api/auth/me");
      if (!res.ok) return null;
      const body = (await res.json()) as { shopper: ShopperUser };
      return body.shopper;
    },
  });

export const boxesQueryOptions = () =>
  queryOptions({
    queryKey: qk.boxes,
    queryFn: () => lootClient.listBoxes(),
    staleTime: 60_000,
  });

export const boxQueryOptions = (boxId: string) =>
  queryOptions({
    queryKey: qk.box(boxId),
    queryFn: () => lootClient.getBox(boxId),
    staleTime: 60_000,
  });

export const balanceQueryOptions = () =>
  queryOptions({
    queryKey: qk.balance,
    queryFn: () => lootClient.getBalance(),
    retry: false,
    staleTime: 15_000,
  });

export const lootMeQueryOptions = () =>
  queryOptions({
    queryKey: qk.lootMe,
    queryFn: () => lootClient.getMe(),
    retry: false,
    staleTime: 60_000,
  });

/** The signed-in shopper's one unclaimed box reveal (or null); the claim modal shows it. */
export const unclaimedRevealQueryOptions = () =>
  queryOptions({
    queryKey: qk.unclaimedReveal,
    queryFn: () => lootFetch<UnclaimedRevealResponse>("/me/reveals/unclaimed"),
    retry: false,
    staleTime: 0,
  });

/** The signed-in shopper's saved shipping address, or null; the account page's card. */
export const shippingAddressQueryOptions = () =>
  queryOptions({
    queryKey: qk.shippingAddress,
    queryFn: () => lootFetch<ShippingAddressResponse>("/me/shipping-address"),
    retry: false,
    staleTime: 60_000,
  });

/** A player as other shoppers see them; the profile dialog. A 404 is an answer, not a retry. */
export const profileQueryOptions = (endUserId: string) =>
  queryOptions({
    queryKey: qk.profile(endUserId),
    queryFn: () => lootClient.getProfile(endUserId),
    retry: false,
    staleTime: 30_000,
  });

/** Packs shoppers can buy: `priceCents` is real money (USD), `creditedCents` what lands in the balance. */
export const balancePacksQueryOptions = () =>
  queryOptions({
    queryKey: qk.balancePacks,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<BalancePack[]> => {
      const res = await fetch("/api/loot/balance/packs");
      // Non-OK (the feature is disabled, or a transient failure) renders as
      // "no packs" rather than an error, as it always has.
      if (!res.ok) return [];
      const body = (await res.json()) as { packs: BalancePack[] };
      return body.packs;
    },
  });

/**
 * The banners live for this visitor, in the store's order (Storefront → Banners in the
 * dashboard). Decoration: a failure renders as no banners rather than an error.
 */
export const bannersQueryOptions = (viewer: string) =>
  queryOptions({
    queryKey: qk.banners(viewer),
    staleTime: 60_000,
    queryFn: async (): Promise<StorefrontBanner[]> => {
      const res = await fetch("/api/storefront/banners");
      if (!res.ok) return [];
      const body = (await res.json()) as { banners: StorefrontBanner[] };
      return body.banners;
    },
  });

export const opensInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: qk.opens,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootClient.listMyOpens({ cursor: pageParam ?? undefined, limit: 20 }),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    staleTime: 30_000,
  });

/**
 * The shopper's inventory, newest first. `status` is part of the key, so the page's filter is a
 * separate cached page rather than a refetch of the same one.
 */
export const inventoryInfiniteQueryOptions = (status: InventoryItemStatus | "all") =>
  infiniteQueryOptions({
    queryKey: qk.inventory(status),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootClient.listMyInventory({
        ...(status === "all" ? {} : { status }),
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 24,
      }),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    staleTime: 30_000,
  });

/** A sale moved the balance and flipped the units it named, in every status filter. */
export function invalidateAfterInventorySale(): void {
  void queryClient.invalidateQueries({ queryKey: ["private", "inventory"] });
  void queryClient.invalidateQueries({ queryKey: qk.balance });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
}

export interface PurchaseStatus {
  status: "pending" | "completed" | "failed" | "refunded" | "disputed";
  /** What the pack credits, integer US cents. */
  creditedCents: number;
  /** The balance once the purchase completed, integer US cents; null while pending. */
  balanceCents: number | null;
}

export type PurchasePollResult =
  | { kind: "unauthorized" }
  | { kind: "retry"; attempt: number }
  | { kind: "status"; status: PurchaseStatus; attempt: number };

/** How many polls before giving up (~20s at 1500ms — covers slow webhooks). */
export const PURCHASE_POLL_LIMIT = 14;

export const purchasePollQueryOptions = (checkoutSessionId: string) =>
  queryOptions({
    queryKey: qk.purchase(checkoutSessionId),
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    // Each result carries its attempt number so both the interval callback
    // and the page can see the poll limit without reaching into query state.
    queryFn: async (): Promise<PurchasePollResult> => {
      const attempt =
        (queryClient.getQueryState(qk.purchase(checkoutSessionId))?.dataUpdateCount ?? 0) + 1;
      const res = await fetch(
        `/api/loot/balance/purchase?sessionId=${encodeURIComponent(checkoutSessionId)}`,
      );
      if (res.status === 401) return { kind: "unauthorized" };
      // Any other non-OK (5xx) keeps polling rather than terminating.
      if (!res.ok) return { kind: "retry", attempt };
      return { kind: "status", status: (await res.json()) as PurchaseStatus, attempt };
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 1500;
      if (data.kind === "unauthorized") return false;
      if (data.kind === "status" && data.status.status !== "pending") return false;
      if (data.attempt >= PURCHASE_POLL_LIMIT) return false;
      return 1500;
    },
  });

export const verificationQueryOptions = (openId: string) =>
  queryOptions({
    queryKey: qk.verification(openId),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const bundle = await lootClient.getOpenVerification(openId);
      // The WebCrypto recompute is deterministic, so caching the pair is sound.
      const result = await verifyOpen(bundle);
      return { bundle, result };
    },
  });

export const seedQueryOptions = (date: string) =>
  queryOptions({
    queryKey: qk.seed(date),
    queryFn: () => lootClient.getSeed(date),
    staleTime: 60_000,
  });

/**
 * In reveal mode the open record and nonce advance at open, and the price moves into
 * `heldCents`, so the spendable balance changes here; the claim moves `balanceCents`.
 */
export function invalidateAfterOpen(): void {
  void queryClient.invalidateQueries({ queryKey: qk.opens });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
  void queryClient.invalidateQueries({ queryKey: qk.balance });
}

export function invalidateAfterClaim(): void {
  invalidateAfterOpen();
}

// ---- battles --------------------------------------------------------------------------

/**
 * `/v1` calls the `LootClient` has no method for, through the same proxy with the same session
 * token and the same one-shot re-mint on a 401 (the client's own behaviour). Throws
 * `LootApiError`, so `isLootApiErrorLike` and `err.code` work as they do for client calls.
 */
export async function lootFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const request = () => {
    const headers = new Headers(init.headers);
    const token = lootClient.getSessionToken();
    if (token) headers.set("authorization", `Bearer ${token}`);
    if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
    headers.set("accept", "application/json");
    return fetch(`${LOOT_PROXY_BASE}/v1${path}`, { ...init, headers });
  };
  let res = await request();
  if (res.status === 401 && lootClient.getSessionToken()) {
    const fresh = await mintFreshSessionToken();
    if (fresh) res = await request();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => undefined)) as
      | { error?: { code?: ErrorCode; message?: string; details?: Record<string, unknown> } }
      | undefined;
    throw new LootApiError(
      body?.error?.code ?? "internal",
      body?.error?.message ?? `request failed with status ${res.status}`,
      res.status,
      body?.error?.details,
    );
  }
  // A 204 has no body to parse; the caller's `T` is `void` or `undefined` for those routes.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * The lobby: public battles plus, with a session, the shopper's own private ones (the session
 * token rides along). Polls every 5 s when there is no socket to tell us about changes.
 */
export const battlesQueryOptions = (opts: {
  viewer: string;
  status?: BattleStatus;
  poll: boolean;
}) =>
  queryOptions({
    queryKey: qk.battles(opts.viewer, opts.status),
    queryFn: async () => {
      const res = await lootFetch<BattleListResponse>(
        `/battles?limit=50${opts.status ? `&status=${opts.status}` : ""}`,
      );
      captureServerTime(res.serverTime);
      return res;
    },
    staleTime: 5_000,
    refetchInterval: opts.poll ? 5_000 : false,
  });

/**
 * One battle, gated by the server. Every fetch re-anchors the server clock. With no socket the
 * page polls once a second until the battle completes; a socket makes `poll` false.
 */
export const battleQueryOptions = (battleId: string, opts: { poll: boolean }) =>
  queryOptions({
    queryKey: qk.battle(battleId),
    queryFn: async () => {
      const res = await lootFetch<BattleResponse>(`/battles/${battleId}`);
      captureServerTime(res.serverTime);
      return res;
    },
    staleTime: 0,
    retry: false,
    refetchInterval: (query) => {
      if (!opts.poll) return false;
      const status = query.state.data?.battle.status;
      return status === "completed" ? false : 1_000;
    },
  });

export const myBattlesInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: qk.myBattles,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootFetch<BattleListResponse>(
        `/me/battles?limit=20${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    staleTime: 30_000,
  });

/**
 * The ticket onto the stage's Event API; fresh until a minute before it expires. Minted with the
 * session when there is one (the BFF forwards it), so a signed-in viewer's token names them.
 */
export const realtimeTokenQueryOptions = (viewer: string) =>
  queryOptions({
    queryKey: qk.realtimeToken(viewer),
    retry: false,
    gcTime: 30 * 60_000,
    staleTime: (query) => {
      const data = query.state.data;
      return data ? Math.max(0, Date.parse(data.expiresAt) - Date.now() - 60_000) : 0;
    },
    queryFn: () => lootFetch<RealtimeTokenResponse>("/realtime/token", { method: "POST" }),
  });

/** After a seat is taken or a battle starts: the lists and, when given, that battle. */
export function invalidateAfterBattleChange(battleId?: string): void {
  void queryClient.invalidateQueries({ queryKey: ["battles", "list"] });
  void queryClient.invalidateQueries({ queryKey: qk.myBattles });
  if (battleId) void queryClient.invalidateQueries({ queryKey: qk.battle(battleId) });
}

/** Create and join debit the entry from the balance the moment the seat is taken. */
export function invalidateAfterBattleEntry(): void {
  void queryClient.invalidateQueries({ queryKey: qk.balance });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
}

/** A claim minted items; a sell-back also credited the balance. */
export function invalidateAfterBattleClaim(battleId: string): void {
  invalidateAfterBattleChange(battleId);
  void queryClient.invalidateQueries({ queryKey: qk.balance });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
}

// ---- forge ----------------------------------------------------------------------------

/** What the builder's item picker searches by: a name and a value range in US cents. */
export interface ForgeItemSearch {
  q: string;
  minCents?: number;
  maxCents?: number;
}

function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/** GET /v1/forge/boxes: the browse grid, newest or most opened first. */
export const forgeBoxesInfiniteQueryOptions = (sort: ForgeBoxSort) =>
  infiniteQueryOptions({
    queryKey: qk.forgeBoxes(sort),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootFetch<ForgeBoxListResponse>(
        `/forge/boxes${queryString({ sort, limit: 24, cursor: pageParam ?? undefined })}`,
      ),
    getNextPageParam: (last) => last.nextCursor,
    staleTime: 30_000,
  });

/** GET /v1/forge/images: the store's preset box images; `[]` = the builder offers none. */
export const forgeImagesQueryOptions = () =>
  queryOptions({
    queryKey: qk.forgeImages,
    queryFn: () => lootFetch<ForgeImagesResponse>("/forge/images"),
    staleTime: 5 * 60_000,
  });

/**
 * GET /v1/forge/items: what a forge box may hold. The first page leads with the Mystery Bulk
 * Card tiers the search matches; later pages are the search alone.
 */
export const forgeItemsInfiniteQueryOptions = (search: ForgeItemSearch) =>
  infiniteQueryOptions({
    queryKey: qk.forgeItems(search),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      lootFetch<ForgeItemsResponse>(
        `/forge/items${queryString({
          q: search.q.trim() || undefined,
          minCents: search.minCents,
          maxCents: search.maxCents,
          limit: 48,
          cursor: pageParam ?? undefined,
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor,
    retry: false,
    staleTime: 60_000,
  });

/** GET /v1/me/forge: the shopper's own forge boxes and their unclaimed earnings. */
export const myForgeQueryOptions = () =>
  queryOptions({
    queryKey: qk.myForge,
    queryFn: () => lootFetch<MyForgeResponse>("/me/forge"),
    retry: false,
    staleTime: 15_000,
  });

/** A forge box was built, edited or had its status set: the grid and the shopper's list. */
export function invalidateAfterForgeChange(boxId?: string): void {
  void queryClient.invalidateQueries({ queryKey: ["forge", "boxes"] });
  void queryClient.invalidateQueries({ queryKey: qk.myForge });
  void queryClient.invalidateQueries({ queryKey: qk.boxes });
  if (boxId) void queryClient.invalidateQueries({ queryKey: qk.box(boxId) });
}

/** A claim credited the balance and zeroed the unclaimed earnings. */
export function invalidateAfterForgeClaim(): void {
  void queryClient.invalidateQueries({ queryKey: qk.myForge });
  void queryClient.invalidateQueries({ queryKey: qk.balance });
  void queryClient.invalidateQueries({ queryKey: qk.lootMe });
}
